/* 1.4.2 merge note (TM-417): the gate listeners below bind on WINDOW capture, not document capture.
 * Razorpay's magic-shopify.js binds its click handler on document in the capture phase and loads
 * first, so a document-capture guard runs AFTER it and cannot stop the portal (measured on 1.3.1).
 * Only window-capture runs before it. Everything else is the 1.4.0 TC-293/TC-235 logic unchanged. */
/*
 * pdp-personalisation.js
 * -------------------------------------------------------------------------
 * PRODUCT-PAGE half of the "Name to be printed" guard. The cart-page half
 * lives in assets/cart-personalisation.js, which is only loaded on the cart -
 * which is why the product page had no validation at all.
 *
 * WHICH PRODUCTS GET VALIDATED
 * -------------------------------------------------------------------------
 * King (ymq) Product Options is the single source of truth. On a personalised
 * product it renders:
 *
 *   <div class="ymq-options-box"
 *        data-validate="required|minchar:1|maxchar:20"
 *        data-label="Name to be printed"
 *        data-name="properties[Name to be printed]"> ... <input class="ymq-input-text">
 *
 * On a product that needs no name - a book, say - King renders NOTHING: zero
 * .ymq-options-box elements. So this script keys off King's own markup and is
 * a complete no-op on non-personalised products. There is no hardcoded product
 * or collection list here to drift out of sync with King's admin.
 *
 * Only boxes whose data-validate contains "required" are enforced; an optional
 * King field (e.g. a gift message) is left alone.
 *
 * TIMING - the thing that makes this subtle
 * -------------------------------------------------------------------------
 * King renders its options ASYNCHRONOUSLY, well after DOMContentLoaded. This
 * script is deferred, so when it first runs a personalised product looks
 * exactly like a non-personalised one: zero boxes. Any "nothing here, give up"
 * shortcut at startup therefore disables the guard permanently. The observer is
 * always attached for that reason; the gate itself no-ops while no boxes exist.
 *
 * WHAT IT GUARDS  (TC-293 — click-time validation for ALL CTAs)
 * -------------------------------------------------------------------------
 * Every CTA is now handled IDENTICALLY: the button stays fully ENABLED and
 * clickable, and validation happens at click time. If a required "Name to be
 * printed" field is empty when the shopper clicks, the click is blocked
 * (preventDefault + stopImmediatePropagation on the capture phase so the real
 * action never fires), the centered Zippy mascot popup is shown, and the first
 * empty required field is focused + scrolled into view. If the name is filled,
 * nothing happens and the real action proceeds normally.
 *
 *   Add to cart      -> click-intercept, mascot nudge + focus, no visual disable.
 *   Buy it now /     -> click-intercept (capture-phase preventDefault +
 *   express /           stopImmediatePropagation on the real
 *   Razorpay /          .shopify-payment-button__button / .magic-buy-btn), mascot
 *   Addly upsell        nudge + focus. NO LONGER dimmed / disabled.
 *   Get Bundle /     -> .paddon__add is guarded here too (single owner);
 *   Add Offer           product-addons.js no longer disables it.
 *
 * Before TC-293 the express Buy Now buttons and .paddon__add were GATED
 * (pointer-events:none + reduced opacity + aria-disabled) while a required
 * field was empty. That grey/disabled state read as a broken page. It is gone:
 * all three CTAs render normally and validate on click, exactly like ATC.
 *
 * A document-level elementsFromPoint backstop is kept for buttons that live in
 * an iframe / portal and cannot receive a direct listener, but for the (now
 * enabled) in-page buttons the direct capture-phase listener is the gate.
 * -------------------------------------------------------------------------
 *
 * TC-251 — STICKY CTA PORTAL REMOVED
 * -------------------------------------------------------------------------
 * TC-244 previously PORTALED the real .tc237-sticky-cta node (the wrapped
 * <product-form> buy buttons) to be a direct child of <body> on ALL viewports.
 * That broke desktop (buttons dumped at the page bottom, outside the info
 * column) and locked mobile scrolling. The portal is GONE: the buy buttons now
 * stay in their natural DOM position on every viewport. On mobile the sticky
 * bar is achieved purely in CSS (assets/tc237-sticky.css) by neutralising the
 * transformed/sticky ancestors inside the <=749px media query so position:fixed
 * re-anchors to the viewport — no DOM move, so page scroll is never trapped.
 *
 * The TC-235 Zippy mascot nudge (.tc235-nudge) keeps its own portal: it is
 * pointer-events:none and a small bottom element, so it can neither trap scroll
 * nor intercept clicks.
 */
(function () {
  var BOX_SEL = '.ymq-options-box[data-validate*="required"]';
  var ALL_BOX_SEL = '.ymq-options-box';
  var FIELD_SEL = 'input.ymq-input-text, textarea.ymq-input-text, [name^="ymq["]';
  var ATC_SEL = 'button[name="add"], .product-form__submit';
  var BYPASS_SEL = [
    '.magic-buy-btn',
    '.magic-buy-btn__parent',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    '.additional-checkout-buttons',
    '.addly_bundle_addToCart',
    '.addly_quickView_addToCart',
    '.n9-fbt-btn-atc',
    '.n9-quickview-btn-custom'
  ].join(',');

  var PORTAL_SEL = [
    '.razorpay-container',
    '.razorpay-backdrop',
    '.razorpay-checkout-frame',
    '#magic-checkout-data-container',
    '#magic-merchant-js'
  ].join(',');

  function boxes() {
    return Array.prototype.slice.call(document.querySelectorAll(BOX_SEL));
  }
  function fieldOf(box) { return box.querySelector(FIELD_SEL); }
  function labelOf(box) { return box.getAttribute('data-label') || 'this field'; }

  function isUnfilled(f) {
    var v = String(f.value == null ? '' : f.value).trim();
    if (!v) return true;
    var hint = String(f.placeholder == null ? '' : f.placeholder).trim();
    return !!hint && v === hint;
  }

  function missing() {
    return boxes().filter(function (b) {
      var f = fieldOf(b);
      return f && isUnfilled(f);
    });
  }

  var MIRROR_ATTR = 'data-cp-mirror';

  function productForms() {
    return Array.prototype.slice.call(
      document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')
    );
  }

  function propKeyOf(box) {
    var dn = box.getAttribute('data-name') || '';
    var m = /^properties\[(.+)\]$/.exec(dn.trim());
    if (m && m[1].trim()) return m[1].trim();
    var lbl = (box.getAttribute('data-label') || '').trim();
    return lbl || null;
  }

  function valueOf(box) {
    var f = fieldOf(box);
    if (!f) return '';
    if (isUnfilled(f)) return '';
    return String(f.value == null ? '' : f.value).trim();
  }

  function mirrorToForm() {
    var allBoxes = Array.prototype.slice.call(document.querySelectorAll(ALL_BOX_SEL));
    if (!allBoxes.length) return;
    productForms().forEach(function (form) {
      allBoxes.forEach(function (box) {
        var key = propKeyOf(box);
        var f = fieldOf(box);
        if (!key || !f) return;
        var name = 'properties[' + key + ']';
        var existing = form.querySelector('[name="' + name.replace(/"/g, '\\"') + '"]');
        var ours = form.querySelector('[' + MIRROR_ATTR + '="' + key.replace(/"/g, '\\"') + '"]');
        if (existing && existing !== ours) {
          if (ours && ours.parentNode) ours.parentNode.removeChild(ours);
          return;
        }
        var val = valueOf(box);
        if (!val) {
          if (ours && ours.parentNode) ours.parentNode.removeChild(ours);
          return;
        }
        if (!ours) {
          ours = document.createElement('input');
          ours.type = 'hidden';
          ours.setAttribute(MIRROR_ATTR, key);
          ours.name = name;
          form.appendChild(ours);
        }
        ours.value = val;
      });
    });
  }

  function errorEl(box) {
    var el = box.querySelector('[data-pdp-cp-error]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-pdp-cp-error', '');
      el.style.cssText =
        'color:#d72c0d;font-size:0.85rem;margin-top:0.35rem;line-height:1.3;';
      box.appendChild(el);
    }
    return el;
  }

  function showError(box, on) {
    var el = errorEl(box);
    if (on) {
      el.textContent = 'Please add “' + labelOf(box) + '” before continuing.';
      el.hidden = false;
      var f = fieldOf(box);
      if (f) f.style.borderColor = '#d72c0d';
    } else {
      el.hidden = true;
      var g = fieldOf(box);
      if (g) g.style.borderColor = '';
    }
  }

  // TC-293 — NO CTA is visually disabled/greyed for the empty-name case anymore.
  // The express Buy Now buttons (BYPASS_SEL) and .paddon__add used to be gated
  // (pointer-events:none + opacity:0.4 + aria-disabled) while a required field
  // was empty; that grey/disabled look read as a broken page. Validation now
  // happens purely at click time (see the capture-phase gate below), identical
  // to Add to Cart. syncGates() is retained as a defensive no-op that also
  // *clears* any stale gate an older build may have left on an element, so the
  // buttons always render fully enabled (normal opacity, pointer-events auto).
  var GATE = 'data-pdp-cp-gated';

  function ungateEl(el) {
    if (!el || !el.hasAttribute(GATE)) return;
    el.style.pointerEvents = el.dataset.pdpPrevPe || '';
    el.style.opacity = el.dataset.pdpPrevOp || '';
    el.setAttribute('aria-disabled', 'false');
    el.removeAttribute(GATE);
    delete el.dataset.pdpPrevPe;
    delete el.dataset.pdpPrevOp;
  }

  function syncGates() {
    // Never disable anything. Only ensure previously-gated elements are cleared.
    Array.prototype.slice.call(document.querySelectorAll('[' + GATE + ']')).forEach(ungateEl);
  }

  var timer = null;
  function scheduleSync() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; syncGates(); mirrorToForm(); }, 120);
  }

  document.addEventListener('input', function (e) {
    if (!e.target.closest) return;
    var box = e.target.closest(BOX_SEL);
    if (!box) return;
    showError(box, false);
    syncGates();
  });

  document.addEventListener('input', function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest(ALL_BOX_SEL)) return;
    mirrorToForm();
  });

  document.addEventListener('focusin', function (e) {
    if (!e.target.closest) return;
    var box = e.target.closest(BOX_SEL);
    if (!box) return;
    var f = fieldOf(box);
    if (!f || f !== e.target) return;
    var v = String(f.value == null ? '' : f.value).trim();
    var hint = String(f.placeholder == null ? '' : f.placeholder).trim();
    if (hint && v === hint) {
      f.value = '';
      syncGates();
    }
  });

  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;
    mirrorToForm();
    if (!boxes().length) return;
    var miss = missing();
    if (!miss.length) return;
    e.preventDefault();
    e.stopPropagation();
    miss.forEach(function (b) { showError(b, true); });
    showMascotNudge();
    var f = fieldOf(miss[0]);
    if (f) { f.scrollIntoView({ behavior: 'smooth', block: 'center' }); f.focus(); }
  }, true);

  // ---- TC-235 / TC-245 Zippy mascot nudge ---------------------------------
  var NUDGE_SEL = '[data-tc235-mascot]';
  var nudgeTimer = null;
  var _mascotPortaled = false;

  function nudgeEl() { return document.querySelector(NUDGE_SEL); }

  function portalMascot() {
    if (_mascotPortaled) return;
    var el = nudgeEl();
    if (!el) return;
    if (el.parentNode !== document.body) document.body.appendChild(el);
    _mascotPortaled = true;
  }

  function showMascotNudge() {
    var el = nudgeEl();
    if (!el) return;
    portalMascot();
    el.hidden = false;
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    el.style.display = 'block';
    el.style.opacity = '1';
    el.style.visibility = 'visible';
    el.removeAttribute('data-tc235-show');
    void el.offsetWidth;
    el.setAttribute('data-tc235-show', '1');
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = setTimeout(function () {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('data-tc235-show');
      el.style.display = '';
      el.style.opacity = '';
      el.style.visibility = '';
      nudgeTimer = null;
    }, 5000);
  }

  function nudgeAndFocus() {
    var miss = missing();
    if (!miss.length) return false;
    showMascotNudge();
    miss.forEach(function (b) { showError(b, true); });
    var f = fieldOf(miss[0]);
    if (f) {
      f.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); }
    }
    return true;
  }

  // Every guarded CTA — Add to Cart, the express Buy Now buttons, and the
  // Get Bundle / Add Offer button — is validated the same way at click time.
  var NUDGE_TARGET_SEL = [ATC_SEL, BYPASS_SEL, '.paddon__add'].join(',');

  function guardedTargetAt(el) {
    if (!el || !el.closest) return null;
    if (el.closest(PORTAL_SEL)) return null;
    return el.closest(NUDGE_TARGET_SEL);
  }

  // ---- THE single click-time gate -----------------------------------------
  // Direct capture-phase listener on any guarded control. Because the buttons
  // are now ENABLED (no pointer-events:none), the real button receives the
  // click and this fires first. If the name is empty we kill the event with
  // stopImmediatePropagation so neither Shopify's dynamic-checkout handler nor
  // the bundle/offer handler ever runs, then nudge + focus. If the name is
  // filled we do nothing and the real action proceeds.
  function directGate(e) {
    if (!e.target.closest) return;
    if (e.target.closest(PORTAL_SEL)) return;
    var btn = e.target.closest(NUDGE_TARGET_SEL);
    if (!btn) return;
    mirrorToForm();
    if (!boxes().length) return;
    if (!missing().length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    nudgeAndFocus();
  }
  window.addEventListener('pointerdown', directGate, true);
  window.addEventListener('click', directGate, true);

  // ---- backstop for buttons a direct listener can't reach ------------------
  // Express buttons can render inside an iframe / be overlaid by a transparent
  // hit layer, so e.target may not resolve to the button. elementsFromPoint at
  // the pointer coords finds the guarded control underneath and blocks it too.
  function documentCaptureNudge(e) {
    if (!boxes().length) return;
    if (!missing().length) return;
    var x = e.clientX, y = e.clientY;
    if (typeof x !== 'number' || typeof y !== 'number') return;
    var stack = [];
    try {
      stack = document.elementsFromPoint(x, y) || [];
    } catch (err) {
      var one = document.elementFromPoint(x, y);
      if (one) stack = [one];
    }
    var hit = null;
    for (var i = 0; i < stack.length; i++) {
      var t = guardedTargetAt(stack[i]);
      if (t) { hit = t; break; }
    }
    if (!hit) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    nudgeAndFocus();
  }
  window.addEventListener('pointerdown', documentCaptureNudge, true);
  window.addEventListener('click', documentCaptureNudge, true);

  function init() {
    portalMascot();
    syncGates();
    try {
      new MutationObserver(scheduleSync)
        .observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* click guards still apply */ }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
